import { useCallback } from 'react';

import { useSelector } from 'react-redux';

import { StreamTypes } from 'store/player/actions.types';
import { RBProgram } from 'store/railsBuilder/reducer.types';
import {
    selectAppSessionId,
    selectVideoSessionId,
} from 'store/session/selectors';

import { VideoItem, VodEditorial } from 'services/data-manager/types';
import { PlaybackMetricsUavParams, UavEvents } from 'services/uav/types';
import {
    MOBILE_PLAYBACK_CONTENT_SOURCE,
    getPlayBackType,
} from 'services/uav/utils';

import useGetBaseUavParams from './useGetBaseUavParams';

type GetPlaybackMetricsParams = Omit<
    PlaybackMetricsUavParams['metadata'],
    | 'appSessionId'
    | 'playbackSessionId'
    | 'contentType'
    | 'contentSource'
    | 'appName'
    | 'appVersion'
    | 'deviceType'
>;

function useGetPlaybackMetricsParams(
    playbackType: StreamTypes | undefined,
    videoItem: VideoItem | undefined,
) {
    const sessionId = useSelector(selectVideoSessionId);
    const appSessionId = useSelector(selectAppSessionId);

    const getBaseUavParams = useGetBaseUavParams();

    const getPlaybackMetricsParams = useCallback(
        (
            metrics: GetPlaybackMetricsParams,
        ): PlaybackMetricsUavParams | undefined => {
            const baseUavParams = getBaseUavParams(UavEvents.PLAYBACK_METRICS);

            if (baseUavParams && videoItem && sessionId && appSessionId) {
                return {
                    ...baseUavParams,
                    metadata: {
                        ...metrics,
                        appSessionId,
                        playbackSessionId: sessionId,
                        contentSource: MOBILE_PLAYBACK_CONTENT_SOURCE,
                        contentType: getPlayBackType(
                            playbackType,
                            videoItem.originalProgram as
                                | VodEditorial
                                | RBProgram,
                        ),
                        editorialID: videoItem.id,
                        ...baseUavParams.metadata,
                    },
                };
            }

            return undefined;
        },
        [getBaseUavParams, videoItem, sessionId, appSessionId, playbackType],
    );

    return {
        getPlaybackMetricsParams,
    };
}

export default useGetPlaybackMetricsParams;
